import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BookOpen, List, AlertTriangle, X } from 'lucide-react';
// Importa il MANUALE_UTENTE.md e il CHANGELOG.md dalla root del repo come testo grezzo (Vite ?raw).
import manualeRaw from '../../../../../MANUALE_UTENTE.md?raw';
import changelogRaw from '../../../../../CHANGELOG.md?raw';
import { useUiStore } from '../../store/ui';

/* =============================================================================
   Vista "Manuali" — layout a colonne, allineato a sinistra e responsive.
   - Sinistra: indice con sotto-argomenti (scroll-spy → evidenzia la sezione a
     video; clic → salta al punto del manuale).
   - Centro: il manuale, allineato a sinistra; su schermi grandi usa più
     larghezza (più testo per riga). I termini del Glossario restano cliccabili
     nei paragrafi: un clic li evidenzia e ne mostra la spiegazione in alto.
   - Destra: colonna Glossario; al passaggio del mouse / clic su un termine, le
     sue occorrenze nel manuale vengono evidenziate.
   Ogni sezione riporta la versione del modulo che documenta: se il manuale è
   più vecchio della versione corrente del modulo, compare un alert.
   ========================================================================== */

// Versione CORRENTE di ciascun modulo (allineata al CHANGELOG.md). Va aggiornata
// quando si modifica un modulo: se il manuale non viene aggiornato di conseguenza,
// la sezione mostra automaticamente l'alert di disallineamento.
// Chiave = numero della sezione del manuale.
const MODULE_VERSIONS: Record<string, string> = {
  '1': '1.0.0', // Accesso
  '2': '2.3.0', // Le viste principali (piattaforma/Home/Feed/Elenco/Inviata)
  '3': '1.6.0', // Aprire e gestire una mail (azioni in riga)
  '4': '2.3.0', // Badge
  '5': '1.1.0', // Tag e categorie (tassonomia AI)
  '6': '2.2.0', // Spam, blocklist e allowlist
  '7': '2.2.0', // Cestino 48 ore
  '8': '1.4.0', // Tema chiaro/scuro
  '9': '2.2.0', // Impostazioni
  '10': '1.0.0', // Suggerimenti
  '11': '1.0.0', // Installazione e primo accesso
  '12': '1.0.0', // Assistente LinkedIn (estensione Chrome)
  '13': '1.0.0', // FAQ (Domande frequenti)
};

// Confronto semver semplice: -1 se a<b, 0 se uguali, 1 se a>b.
function cmpSemver(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) < (pb[i] ?? 0) ? -1 : 1;
  }
  return 0;
}

// Ultima versione della piattaforma dal changelog (primo "## [x.y.z]").
function latestPlatformVersion(): string {
  for (const raw of changelogRaw.split('\n')) {
    const m = raw.match(/^##\s*\[([0-9]+\.[0-9]+\.[0-9]+)\]/);
    if (m) return m[1];
  }
  return '';
}

type GlossEntry = { id: string; label: string; def: string; tokens: string[] };
type SubItem = { id: string; title: string };
type Section = {
  id: string;
  num: string;
  title: string;
  docVer: string;
  moduleVer: string;
  stale: boolean;
  subs: SubItem[];
  body: ReactNode[];
};

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Rimuove il markup **grassetto**/`codice` per il pannello spiegazione.
function plainDef(def: string): string {
  return def.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/`([^`]+)`/g, '$1');
}

export function ManualiView() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Sezione/paragrafo a cui scrollare all'apertura (es. 'linkedin' per le FAQ
  // dell'Assistente LinkedIn), impostato da altre viste prima di navigare qui.
  const manualiTarget = useUiStore((s) => s.manualiTarget);
  const setManualiTarget = useUiStore((s) => s.setManualiTarget);
  const manualiScope = useUiStore((s) => s.manualiScope);

  const [activeId, setActiveId] = useState<string>('');
  const [active, setActive] = useState<GlossEntry | null>(null); // termine "fissato"
  const [pop, setPop] = useState<{ x: number; y: number } | null>(null); // posizione popover
  const [hoverId, setHoverId] = useState<string | null>(null); // hover dalla colonna
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeEntryId = active?.id ?? hoverId;

  const closePop = () => {
    setActive(null);
    setPop(null);
  };

  const platformVersion = useMemo(() => latestPlatformVersion(), []);

  // 1) Estrae le voci del Glossario dalla sezione "## Glossario".
  const glossary = useMemo<GlossEntry[]>(() => {
    const out: GlossEntry[] = [];
    let inGloss = false;
    for (const raw of manualeRaw.split('\n')) {
      const line = raw.trim();
      if (line.startsWith('## ')) {
        inGloss = /glossario/i.test(line);
        continue;
      }
      if (!inGloss) continue;
      const m = line.match(/^-\s+\*\*(.+?)\*\*\s*[—–-]\s*(.+)$/);
      if (m) {
        const label = m[1].trim();
        const tokens = Array.from(
          new Set(
            label
              .split('/')
              .map((t) => t.trim().toLowerCase())
              .filter((t) => t.length >= 2),
          ),
        );
        out.push({ id: slugify(label), label, def: m[2].trim(), tokens });
      }
    }
    return out;
  }, []);

  // Mappa token→voce + regex unica (confini di parola unicode-aware).
  const { glossRe, tokenToEntry } = useMemo(() => {
    const tokenToEntry = new Map<string, GlossEntry>();
    const all: string[] = [];
    for (const g of glossary) {
      for (const t of g.tokens) {
        tokenToEntry.set(t, g);
        all.push(t);
      }
    }
    all.sort((a, b) => b.length - a.length);
    const glossRe = all.length
      ? new RegExp(`(?<![\\p{L}\\p{N}_])(${all.map(escapeRe).join('|')})(?![\\p{L}\\p{N}_])`, 'giu')
      : null;
    return { glossRe, tokenToEntry };
  }, [glossary]);

  // Attiva/disattiva un termine e mostra la spiegazione in un popover ancorato
  // all'elemento cliccato (parola nel testo o voce del glossario).
  function toggleEntry(id: string, anchor?: HTMLElement | null) {
    setActive((prev) => {
      if (prev?.id === id) {
        setPop(null);
        return null;
      }
      if (anchor) {
        const r = anchor.getBoundingClientRect();
        setPop({ x: r.left, y: r.bottom });
      } else {
        setPop(null);
      }
      return glossary.find((g) => g.id === id) ?? null;
    });
  }

  // Spezza un testo rendendo cliccabili i termini del glossario.
  function tokenize(text: string, bold: boolean, kb: string): ReactNode[] {
    const out: ReactNode[] = [];
    let i = 0;
    const pushPlain = (t: string) => {
      if (!t) return;
      out.push(
        bold ? (
          <strong key={`${kb}-b${i++}`} className="font-semibold text-zinc-100">
            {t}
          </strong>
        ) : (
          <span key={`${kb}-t${i++}`}>{t}</span>
        ),
      );
    };
    if (!glossRe) {
      pushPlain(text);
      return out;
    }
    let last = 0;
    let m: RegExpExecArray | null;
    glossRe.lastIndex = 0;
    while ((m = glossRe.exec(text)) !== null) {
      const word = m[0];
      const start = m.index;
      if (start > last) pushPlain(text.slice(last, start));
      const entry = tokenToEntry.get(word.toLowerCase());
      const isActive = !!entry && entry.id === activeEntryId;
      out.push(
        <button
          key={`${kb}-g${i++}`}
          type="button"
          data-entry={entry?.id}
          onClick={(e) => {
            e.stopPropagation();
            if (entry) toggleEntry(entry.id, e.currentTarget);
          }}
          title="Glossario: clic per la spiegazione"
          className={
            '-mx-0.5 rounded px-0.5 underline decoration-dotted underline-offset-2 transition ' +
            (bold ? 'font-semibold ' : '') +
            (isActive
              ? 'bg-amber-400 text-zinc-950 no-underline'
              : 'text-accent-200 decoration-accent-400/50 hover:bg-accent-400/10')
          }
        >
          {word}
        </button>,
      );
      last = start + word.length;
      if (glossRe.lastIndex === start) glossRe.lastIndex++;
    }
    if (last < text.length) pushPlain(text.slice(last));
    return out;
  }

  // Rende **grassetto**, `codice`, link markdown, poi applica il glossario.
  function renderInline(text: string, kb: string): ReactNode[] {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]*\))/g);
    const nodes: ReactNode[] = [];
    parts.forEach((p, idx) => {
      if (!p) return;
      const k = `${kb}-${idx}`;
      if (p.startsWith('**') && p.endsWith('**')) {
        nodes.push(...tokenize(p.slice(2, -2), true, k));
      } else if (p.startsWith('`') && p.endsWith('`')) {
        nodes.push(
          <code key={k} className="rounded bg-zinc-800/80 px-1 py-0.5 text-[0.85em] text-accent-200">
            {p.slice(1, -1)}
          </code>,
        );
      } else {
        const link = p.match(/^\[([^\]]+)\]\([^)]*\)$/);
        nodes.push(...tokenize(link ? link[1] : p, false, k));
      }
    });
    return nodes;
  }

  // 2) Costruisce le sezioni del manuale (esclude il Glossario dal corpo).
  const { intro, sections } = useMemo(() => {
    const intro: ReactNode[] = [];
    const sections: Section[] = [];
    let current: Section | null = null;
    let list: ReactNode[] = [];
    let key = 0;
    let inGloss = false;

    const target = () => (current ? current.body : intro);
    const flushList = () => {
      if (list.length) {
        target().push(
          <ul key={key++} className="mb-3 space-y-1.5 pl-1">
            {list}
          </ul>,
        );
        list = [];
      }
    };

    for (const raw of manualeRaw.split('\n')) {
      const line = raw.trimEnd();
      const t = line.trim();

      if (t.startsWith('# ')) continue;
      if (t === '---') {
        flushList();
        continue;
      }
      if (t.startsWith('## ')) {
        flushList();
        const heading = t.slice(3).trim();
        if (/glossario/i.test(heading)) {
          inGloss = true;
          current = null;
          continue;
        }
        inGloss = false;
        const nm = heading.match(/^(\d+)\.\s*(.*)$/);
        const num = nm ? nm[1] : '';
        const title = nm ? nm[2] : heading;
        const moduleVer = MODULE_VERSIONS[num] ?? '';
        current = {
          id: 'sez-' + (num || slugify(title)),
          num,
          title,
          docVer: '',
          moduleVer,
          stale: false,
          subs: [],
          body: [],
        };
        sections.push(current);
        continue;
      }
      if (inGloss) continue;

      const vm = t.match(/^<!--\s*ver:\s*([0-9.]+)\s*-->$/);
      if (vm && current) {
        current.docVer = vm[1];
        current.stale = !!current.moduleVer && cmpSemver(current.docVer, current.moduleVer) < 0;
        continue;
      }
      if (t.startsWith('<!--')) continue;

      if (t.startsWith('> ')) {
        flushList();
        target().push(
          <p
            key={key++}
            className="mb-4 rounded-lg border border-accent-400/30 bg-accent-400/5 p-3 text-sm text-zinc-300"
          >
            {renderInline(t.slice(2), `q${key}`)}
          </p>,
        );
        continue;
      }
      if (t.startsWith('### ')) {
        flushList();
        if (!current) continue;
        const st = t.slice(4).trim();
        const subId = current.id + '-' + slugify(st);
        current.subs.push({ id: subId, title: st });
        current.body.push(
          <h3 key={key++} id={subId} className="mb-2 mt-6 scroll-mt-4 text-[15px] font-semibold text-zinc-100">
            {renderInline(st, `h3${key}`)}
          </h3>,
        );
        continue;
      }
      if (t.startsWith('- ')) {
        list.push(
          <li key={key++} className="flex gap-2 text-sm text-zinc-300">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-zinc-600" />
            <span>{renderInline(t.slice(2), `li${key}`)}</span>
          </li>,
        );
        continue;
      }
      flushList();
      if (t) {
        target().push(
          <p key={key++} className="mb-3 max-w-[72ch] text-sm leading-relaxed text-zinc-400">
            {renderInline(t, `p${key}`)}
          </p>,
        );
      }
    }
    flushList();
    return { intro, sections };
    // Ricalcola quando cambia il termine attivo (aggiorna l'evidenziazione inline).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEntryId, glossRe, tokenToEntry]);

  const anyStale = sections.some((s) => s.stale);

  // Manuale PER-SEZIONE: se lo scope è 'linkedin' mostra solo le sezioni
  // dell'Assistente LinkedIn; altrimenti il manuale generale webmail (escluse
  // quelle LinkedIn). Così i manuali delle diverse sezioni non si confondono.
  const isLinkedInSection = (t: string) => /linkedin/i.test(t);
  const visibleSections = useMemo(
    () =>
      manualiScope === 'linkedin'
        ? sections.filter((s) => isLinkedInSection(s.title))
        : sections.filter((s) => !isLinkedInSection(s.title)),
    [sections, manualiScope],
  );

  // Scroll automatico a una sezione/sottosezione target (es. 'linkedin' per le
  // FAQ dell'Assistente LinkedIn), impostato da altre viste (LinkedInView,
  // ExtensionTab) prima di aprire il manuale. Le sezioni di questa vista sono
  // sempre renderizzate espanse (non esiste un accordion da aprire): basta
  // scrollare all'elemento con l'id trovato. Il target viene azzerato subito
  // dopo per non riscrollare ad ogni render successivo.
  useEffect(() => {
    if (!manualiTarget) return;
    if (sections.length === 0) return; // manuale non ancora pronto

    const target = manualiTarget.trim().toLowerCase();
    if (!target) {
      setManualiTarget(null);
      return;
    }

    // Candidati ricercabili: ogni sezione e ogni sua sottosezione, con testo
    // = id (già uno slug) + titolo, in minuscolo.
    const candidates: { id: string; text: string }[] = [];
    for (const s of sections) {
      candidates.push({ id: s.id, text: `${s.id} ${s.title}`.toLowerCase() });
      for (const sub of s.subs) {
        candidates.push({ id: sub.id, text: `${sub.id} ${sub.title}`.toLowerCase() });
      }
    }

    // Priorità di ricerca: per il target 'linkedin' punta prima alla
    // sottosezione che parla ESPLICITAMENTE di FAQ + LinkedIn (es. "FAQ —
    // Assistente LinkedIn"), poi a un match più largo su "linkedin", poi come
    // ultima spiaggia alla sezione FAQ generale ("faq"). Per qualunque altro
    // target si prova un match diretto sulla stringa passata.
    const tiers: string[][] = target.includes('linkedin')
      ? [['assistente', 'linkedin'], ['linkedin'], ['faq', 'linkedin'], ['faq']]
      : [[target]];

    let found: { id: string; text: string } | undefined;
    for (const words of tiers) {
      found = candidates.find((c) => words.every((w) => c.text.includes(w)));
      if (found) break;
    }

    // Nessuna corrispondenza: nessuno scroll, comportamento identico a oggi.
    if (!found) {
      setManualiTarget(null);
      return;
    }

    const el = document.getElementById(found.id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setManualiTarget(null);
  }, [manualiTarget, sections, setManualiTarget]);

  // Scroll-spy: evidenzia nell'indice la sezione/sotto-sezione visibile.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const ids: string[] = [];
    for (const s of visibleSections) {
      ids.push(s.id);
      for (const sub of s.subs) ids.push(sub.id);
    }
    const onScroll = () => {
      const base = scroller.getBoundingClientRect().top;
      let cur = ids[0] ?? '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - base < 130) cur = id;
      }
      setActiveId(cur);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [visibleSections]);

  const goTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };

  // ---- Sotto-componenti riusati per mobile e desktop ----
  const IndexNav = () => (
    <nav className="text-sm">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        <List size={13} /> Indice
      </div>
      <ul className="space-y-0.5">
        {visibleSections.map((s) => (
          <li key={s.id}>
            <button
              onClick={() => goTo(s.id)}
              className={
                'flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors ' +
                (activeId === s.id || s.subs.some((x) => x.id === activeId)
                  ? 'bg-accent-500/15 text-accent-300'
                  : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200')
              }
            >
              {s.num && <span className="text-zinc-600">{s.num}.</span>}
              <span className="flex-1 truncate">{s.title}</span>
              {s.stale && <AlertTriangle size={12} className="shrink-0 text-amber-300" />}
            </button>
            {s.subs.length > 0 && (
              <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-zinc-800/80 pl-2">
                {s.subs.map((sub) => (
                  <li key={sub.id}>
                    <button
                      onClick={() => goTo(sub.id)}
                      className={
                        'w-full truncate rounded px-2 py-0.5 text-left text-[13px] transition-colors ' +
                        (activeId === sub.id ? 'text-accent-300' : 'text-zinc-500 hover:text-zinc-300')
                      }
                    >
                      {sub.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );

  const GlossaryPanel = () => (
    <div className="text-sm">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        <BookOpen size={13} /> Glossario
      </div>
      <p className="mb-3 text-[12px] leading-snug text-zinc-500">
        Passa il mouse o clicca un termine per evidenziarlo nel manuale.
      </p>
      <ul className="space-y-1">
        {glossary.map((g) => {
          const on = activeEntryId === g.id;
          return (
            <li key={g.id}>
              <button
                onMouseEnter={() => setHoverId(g.id)}
                onMouseLeave={() => setHoverId(null)}
                onClick={(e) => toggleEntry(g.id, e.currentTarget)}
                className={
                  'w-full rounded-md border px-2.5 py-1.5 text-left transition-colors ' +
                  (on
                    ? 'border-accent-500/40 bg-accent-500/15'
                    : 'border-transparent hover:border-zinc-700 hover:bg-zinc-800/60')
                }
              >
                <div
                  className={
                    'flex items-center gap-1.5 text-[13px] font-semibold ' +
                    (on ? 'text-accent-300' : 'text-zinc-200')
                  }
                >
                  {g.label}
                  {active?.id === g.id && <X size={12} className="text-zinc-500" />}
                </div>
                {on && <div className="mt-1 text-[12px] leading-snug text-zinc-400">{plainDef(g.def)}</div>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="flex flex-1 overflow-hidden">
      <main ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          {/* Intestazione */}
          <div className="mb-1 flex items-center gap-2">
            <BookOpen size={20} className="text-accent-300" />
            <h1 className="text-xl font-bold text-zinc-100">
              {manualiScope === 'linkedin' ? 'Manuale — Assistente LinkedIn' : 'Manuali'}
            </h1>
          </div>
          <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-500">
            <span>
              {manualiScope === 'linkedin'
                ? "Guida dell'Assistente LinkedIn (estensione Chrome). Le parole del glossario sono cliccabili."
                : "Guida d'uso della piattaforma. Le parole del glossario sono cliccabili."}
            </span>
            {platformVersion && (
              <span className="rounded-full bg-zinc-800/80 px-2 py-0.5 text-[11px] text-zinc-400">
                Piattaforma v{platformVersion}
              </span>
            )}
          </div>

          {anyStale && (
            <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-700/50 bg-amber-950/50 px-3 py-2 text-[13px] text-amber-300">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                Alcune sezioni documentano una versione precedente del modulo: controlla gli avvisi
                accanto ai titoli.
              </span>
            </div>
          )}

          {/* Barra mobile: apre indice e glossario (sotto xl) */}
          <div className="mb-4 xl:hidden">
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800/60"
            >
              <List size={15} /> {manualiScope === 'linkedin' ? 'Indice' : 'Indice e glossario'}
            </button>
            {mobileOpen && (
              <div className={'mt-3 grid gap-6 rounded-lg border border-zinc-800/80 bg-zinc-900/40 p-4 ' + (manualiScope === 'linkedin' ? '' : 'sm:grid-cols-2')}>
                <IndexNav />
                {manualiScope !== 'linkedin' && <GlossaryPanel />}
              </div>
            )}
          </div>

          {/* Griglia principale: indice | manuale | glossario */}
          <div className={'grid grid-cols-1 gap-8 ' + (manualiScope === 'linkedin' ? 'xl:grid-cols-[220px_minmax(0,1fr)]' : 'xl:grid-cols-[220px_minmax(0,1fr)_270px]')}>
            <aside className="hidden xl:block">
              <div className="sticky top-0 max-h-screen overflow-y-auto pb-8 pr-1">
                <IndexNav />
              </div>
            </aside>

            <article ref={bodyRef} className="min-w-0">
              {manualiScope !== 'linkedin' && intro}
              {visibleSections.map((s) => (
                <section key={s.id} id={s.id} className="mt-10 scroll-mt-4 first:mt-2">
                  <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-800/80 pb-2">
                    <h2 className="text-lg font-bold text-accent-300">
                      {s.num && <span className="mr-1 text-zinc-500">{s.num}.</span>}
                      {s.title}
                    </h2>
                    {s.docVer &&
                      (s.stale ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-amber-700/50 bg-amber-950/50 px-2 py-0.5 text-[11px] font-medium text-amber-300"
                          title={`Il manuale documenta la v${s.docVer}, ma il modulo è aggiornato alla v${s.moduleVer}.`}
                        >
                          <AlertTriangle size={11} /> doc v{s.docVer} · modulo v{s.moduleVer}
                        </span>
                      ) : (
                        <span
                          className="rounded-full bg-zinc-800/80 px-2 py-0.5 text-[11px] font-medium text-zinc-400"
                          title={`Sezione allineata alla versione del modulo (v${s.moduleVer}).`}
                        >
                          v{s.docVer}
                        </span>
                      ))}
                  </div>
                  {s.body}
                </section>
              ))}
            </article>

            {manualiScope !== 'linkedin' && (
              <aside className="hidden xl:block">
                <div className="sticky top-0 max-h-screen overflow-y-auto pb-8 pl-1">
                  <GlossaryPanel />
                </div>
              </aside>
            )}
          </div>
        </div>

        {/* Popover spiegazione del termine, ancorato al punto cliccato (sempre visibile). */}
        {active && pop && (
          <>
            <button
              aria-label="Chiudi spiegazione"
              onClick={closePop}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              className="fixed z-50 w-[min(320px,88vw)] rounded-lg border border-amber-400/60 bg-zinc-900 p-3 shadow-xl"
              style={{
                left: Math.max(
                  8,
                  Math.min(pop.x, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 330),
                ),
                top: pop.y + 6,
              }}
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400" />
                <span className="text-sm font-semibold text-amber-300">{active.label}</span>
                <button
                  type="button"
                  onClick={closePop}
                  title="Chiudi"
                  className="ml-auto rounded p-0.5 text-zinc-400 transition hover:text-zinc-200"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="text-[13px] leading-snug text-zinc-300">{plainDef(active.def)}</div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
